import { useState, useEffect } from 'react';
import { normalizeUser } from '@/services/normalize-user.service';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import type { BusinessRegisterData } from '@/services/normalize-user.service';
import { createClient } from '@/services/supabase';


type RegisterFormData = {
  email: string;
  password: string;
  confirmPassword: string;
  business_name: string;
  business_type: string;
  phone?: string;
  parish?: string;
  role?: string;
  userType: 'business' | 'charity';
  is_approved: boolean;
};

const SuperRegisterPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{show: boolean, message: string, user: any, email: string}>({show: false, message: '', user: null, email: ''});
  
  const [, setIsAuthenticated] = useState(false);
  
  // Check if user is authenticated as super admin
  useEffect(() => {
    const checkSuperAdminAuth = async () => {
      const supabase = createClient();
      const { data: { session } } = await (await supabase).auth.getSession();
      if (session?.user) {
        // Use normalizeUser to create a consistent user object
        console.debug('Using normalizeUser in SuperRegister checkSuperAdminAuth');
        const normalizedUser = normalizeUser(session.user);
        
        // Get role from normalized user
        const userRole = normalizedUser.role;
        
        // Keep original implementation for backward compatibility
        
        if (userRole === 'super_admin') {
          setIsAuthenticated(true);
        } else {
          navigate('/super-login');
        }
      } else {
        navigate('/super-login');
      }
    };
    
    checkSuperAdminAuth();
  }, [navigate]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<RegisterFormData>({
    defaultValues: {
      userType: 'business',
      is_approved: true
    }
  });

  const password = watch('password', '');
  
  const onSubmit = async (formData: RegisterFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate password match
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      
      // Direct registration to Supabase bypassing normal business validation
      const registerData: BusinessRegisterData = {
        email: formData.email,
        password: formData.password,
        business_name: formData.business_name,
        business_type: formData.business_type || '',
        phone: formData.phone || '',
        parish: formData.parish || '',
        role: formData.role || (formData.userType === 'business' ? 'owner' : 'admin'),
        is_approved: formData.is_approved
      };
      
      // Register user directly with Supabase
      const supabase = await createClient();
      
      // Register the user with Supabase
      const { data, error } = await supabase.auth.signUp({
        email: registerData.email,
        password: registerData.password,
        options: {
          data: {
            name: registerData.business_name,
            role: 'business',
            business_id: registerData.business_id,
            business_name: registerData.business_name,
            business_type: registerData.business_type,
            parish: registerData.parish,
            phone: registerData.phone,
            is_approved: registerData.is_approved
          }
        }
      });
      
      if (error) throw error;
      
      const normalizedUser = data.user ? normalizeUser(data.user) : null;
      
      if (normalizedUser) {
        // Show success message with the email for reference
        setSuccess({
          show: true,
          message: `User ${registerData.email} has been successfully registered.`,
          user: normalizedUser,
          email: registerData.email
        });
        
        // Reset form fields
        setValue('email', '');
        setValue('password', '');
        setValue('confirmPassword', '');
        setValue('business_name', '');
        setValue('business_type', '');
        setValue('phone', '');
        setValue('parish', '');
        setValue('role', formData.userType === 'business' ? 'owner' : 'admin');
        
        // Reset the form state
        const formElement = document.getElementById('registerForm') as HTMLFormElement;
        if (formElement) formElement.reset();
        
        toast.success('User registered successfully');
        
        // Redirect based on user type if needed
        // This is commented out to allow multiple registrations without navigating away
        /*
        if (data.userType === 'business') {
          navigate('/dashboard');
        } else if (data.userType === 'charity') {
          navigate('/charity/dashboard');
        }
        */
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to register user');
      toast.error('Registration failed: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Register New User
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Create a new user account with super admin privileges
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {success.show ? (
            <div className="rounded-md bg-green-50 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Registration Successful</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>{success.message}</p>
                    <p className="mt-1 font-semibold">User can now log in with their credentials.</p>
                  </div>
                  <div className="mt-4">
                    <div className="-mx-2 -my-1.5 flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setSuccess({show: false, message: '', user: null, email: ''})}
                        className="bg-green-50 px-2 py-1.5 rounded-md text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        Register Another
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Copy email to clipboard for easy sharing
                          navigator.clipboard.writeText(success.email);
                          toast.info('Email copied to clipboard');
                        }}
                        className="bg-blue-50 px-2 py-1.5 rounded-md text-sm font-medium text-blue-800 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Copy Email
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/super-admin/dashboard')}
                        className="ml-3 bg-green-50 px-2 py-1.5 rounded-md text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        Go to Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form id="registerForm" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="rounded-md bg-red-50 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414-1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Registration Error</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* User Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    User Type
                  </label>
                  <div className="mt-1 flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        {...register('userType')}
                        value="business"
                        className="focus:ring-sky-blue h-4 w-4 text-sky-blue border-gray-300"
                      />
                      <span className="ml-2 text-sm text-dark-gray">Business</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        {...register('userType')}
                        value="charity"
                        className="focus:ring-sky-blue h-4 w-4 text-sky-blue border-gray-300"
                      />
                      <span className="ml-2 text-sm text-dark-gray">Charity</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    placeholder="email@example.com"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    placeholder="••••••••"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    placeholder="••••••••"
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === password || 'Passwords do not match',
                    })}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message as string}</p>
                  )}
                </div>

                {/* Business fields */}
                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Business Name
                  </label>
                  <input
                    type="text"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    placeholder="Business Name"
                    {...register('business_name', {
                      required: 'Business name is required',
                    })}
                  />
                  {errors.business_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.business_name.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Business Type
                  </label>
                  <select
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    {...register('business_type', { required: 'Business type is required' })}
                  >
                    <option value="">Select Business Type</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="grocery">Grocery Store</option>
                    <option value="supermarket">Supermarket</option>
                    <option value="bakery">Bakery</option>
                    <option value="farm">Farm</option>
                    <option value="fishery">Fishery</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.business_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.business_type.message as string}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Parish
                  </label>
                  <select
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    {...register('parish')}
                  >
                    <option value="">Select Parish</option>
                    <option value="christ_church">Christ Church</option>
                    <option value="saint_andrew">Saint Andrew</option>
                    <option value="saint_george">Saint George</option>
                    <option value="saint_james">Saint James</option>
                    <option value="saint_john">Saint John</option>
                    <option value="saint_joseph">Saint Joseph</option>
                    <option value="saint_lucy">Saint Lucy</option>
                    <option value="saint_michael">Saint Michael</option>
                    <option value="saint_peter">Saint Peter</option>
                    <option value="saint_philip">Saint Philip</option>
                    <option value="saint_thomas">Saint Thomas</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    placeholder="(246) 555-5555"
                    {...register('phone')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-gray mb-1">
                    Role
                  </label>
                  <select
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                    {...register('role')}
                  >
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_approved"
                    className="h-4 w-4 text-sky-blue focus:ring-sky-blue border-gray-300 rounded"
                    {...register('is_approved')}
                  />
                  <label htmlFor="is_approved" className="ml-2 block text-sm text-dark-gray">
                    Auto-approve account (bypass normal approval process)
                  </label>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue ${
                      isLoading ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {isLoading ? 'Creating account...' : 'Register User'}
                  </button>
                </div>
              </div>
            </form>
          )}
          
          <div className="mt-6 text-center">
            <p className="text-sm text-dark-gray">
              Back to{' '}
              <Link to="/login" className="font-medium text-sky-blue hover:text-teal transition-colors duration-200">
                Regular Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperRegisterPage;
